import { useCallback, useState } from 'react';

import type { NetworkStats } from '@/api/statsApi';

export type StatId = keyof NetworkStats;

export type StatCategory = 'mostFrequent' | 'originNoDig' | 'pipeline';

export interface StatDefinition {
    id: StatId;
    label: string;
    category: StatCategory;
    format: 'integer' | 'volume';
    /**
     * Exists only in the estate-wide Network Explorer view: never offered in the per-user
     * stats configuration, never part of a single client's defaults or stored preferences.
     */
    explorerOnly?: boolean;
}

export const STAT_DEFINITIONS: StatDefinition[] = [
    // First so the explorer bar leads with it, matching the agreed design.
    {
        id: 'totalNetworks',
        label: 'Total Networks (Clients)',
        category: 'mostFrequent',
        format: 'integer',
        explorerOnly: true,
    },
    { id: 'leaksFound', label: 'Leaks Found', category: 'mostFrequent', format: 'integer' },
    { id: 'totalFixed', label: 'Leaks Repaired', category: 'mostFrequent', format: 'integer' },
    { id: 'potentialSaving', label: 'Volume Identified (Ml/D)', category: 'mostFrequent', format: 'volume' },
    { id: 'waterSaved', label: 'Volume Saved (Ml/D)', category: 'mostFrequent', format: 'volume' },
    { id: 'totalPoisGenerated', label: "POI's Generated", category: 'mostFrequent', format: 'integer' },
    { id: 'assessedPoisCount', label: "POI's Visited", category: 'mostFrequent', format: 'integer' },
    { id: 'inconclusivePoisCount', label: "POI's Inconclusive", category: 'mostFrequent', format: 'integer' },
    { id: 'alreadyMarkedUpPoisCount', label: "POI's Already Marked Up", category: 'mostFrequent', format: 'integer' },
    { id: 'unableToCheckPoisCount', label: "POI's Unable to Check", category: 'mostFrequent', format: 'integer' },
    {
        id: 'awaitingAssessmentPoisCount',
        label: "POI's Awaiting Assessment",
        category: 'mostFrequent',
        format: 'integer',
    },
    { id: 'customerSideLeakageCount', label: 'Customer Side Leakage', category: 'mostFrequent', format: 'integer' },
    { id: 'suitableForNoDigCount', label: 'Suitable For Origin No Dig®', category: 'originNoDig', format: 'integer' },
    { id: 'noDigFixes', label: 'Fixed By Origin No Dig®', category: 'originNoDig', format: 'integer' },
    { id: 'mainsPipesCount', label: 'Mains Pipes', category: 'pipeline', format: 'integer' },
    { id: 'mainsFittingsCount', label: 'Mains Fittings', category: 'pipeline', format: 'integer' },
    { id: 'communicationPipesCount', label: 'Communication Pipes', category: 'pipeline', format: 'integer' },
    { id: 'stopTapBoundaryBoxCount', label: 'Stop Tap / Boundary Box', category: 'pipeline', format: 'integer' },
];

/**
 * The agreed six the Network Explorer header shows until the user changes it, in design
 * order (the bar renders in STAT_DEFINITIONS order, which agrees). The explorer's defaults,
 * not a fixed set: its header is configured exactly like Find & Fix's, under its own
 * preference scope.
 */
export const EXPLORER_STATS: readonly StatId[] = [
    'totalNetworks',
    'leaksFound',
    'potentialSaving',
    'totalPoisGenerated',
    'assessedPoisCount',
    'noDigFixes',
];

// Enabled on first load for a clean first-time user. Render order in the stats bar is
// derived from STAT_DEFINITIONS, not this list, so these ids display in definition order:
// Leaks Found, Volume Identified, POI's Generated, POI's Visited, POI's Awaiting Assessment,
// Suitable For Origin No Dig®, Fixed By Origin No Dig®.
// A stat left out of this list is off by default and stays off for users with stored
// preferences (loadPreferences only ever strips unknown ids, never adds), so introducing an
// off-by-default stat needs no STORAGE_KEY bump — that is how Leaks Repaired / Volume Saved ship.
const DEFAULT_VISIBLE_STATS: StatId[] = [
    'leaksFound',
    'potentialSaving',
    'totalPoisGenerated',
    'assessedPoisCount',
    'awaitingAssessmentPoisCount',
    'suitableForNoDigCount',
    'noDigFixes',
];

/**
 * Which header the preference belongs to. The two are kept apart on purpose: a set chosen
 * for an estate-wide audience in the Network Explorer must not change what a user sees in a
 * client's Find & Fix, and the explorer-only statistics have no single-client meaning.
 */
export type StatsPreferenceScope = 'client' | 'explorer';

interface ScopeConfig {
    storageKey: string;
    /** Ids a stored preference may contain; anything else is stripped on load. */
    allowedIds: StatId[];
    defaults: readonly StatId[];
}

const SCOPE_CONFIG: Record<StatsPreferenceScope, ScopeConfig> = {
    client: {
        storageKey: 'orbit.stats.preferences.v1',
        // Explorer-only stats are not single-client preferences: a stored id for one is unknown here.
        allowedIds: STAT_DEFINITIONS.filter((d) => !d.explorerOnly).map((d) => d.id),
        defaults: DEFAULT_VISIBLE_STATS,
    },
    explorer: {
        storageKey: 'orbit.stats.preferences.explorer.v1',
        allowedIds: STAT_DEFINITIONS.map((d) => d.id),
        defaults: EXPLORER_STATS,
    },
};

function loadPreferences(config: ScopeConfig): StatId[] {
    const defaults = [...config.defaults];
    try {
        const stored = localStorage.getItem(config.storageKey);
        if (!stored) return defaults;
        const parsed: unknown = JSON.parse(stored);
        if (!parsed || typeof parsed !== 'object' || !('visibleStats' in parsed)) return defaults;
        const visibleStats = parsed.visibleStats;
        if (!Array.isArray(visibleStats)) return defaults;
        const valid = visibleStats.filter((id): id is StatId => config.allowedIds.includes(id as StatId));
        return valid.length > 0 ? valid : defaults;
    } catch {
        return defaults;
    }
}

function savePreferences(config: ScopeConfig, visibleStats: StatId[]): void {
    try {
        localStorage.setItem(config.storageKey, JSON.stringify({ visibleStats }));
    } catch {
        // silent fail — private browsing or quota exceeded
    }
}

export function useStatsPreferences(scope: StatsPreferenceScope = 'client'): {
    visibleStats: StatId[];
    toggleStat: (id: StatId, enabled: boolean) => void;
} {
    const config = SCOPE_CONFIG[scope];
    const [visibleStats, setVisibleStats] = useState<StatId[]>(() => loadPreferences(config));

    const toggleStat = useCallback(
        (id: StatId, enabled: boolean) => {
            setVisibleStats((prev) => {
                if (enabled) {
                    // Append to the end; StatsBar derives render order from STAT_DEFINITIONS
                    // independently, so the order of visibleStats values is irrelevant to the UI.
                    const next = [...prev, id];
                    savePreferences(config, next);
                    return next;
                }
                if (prev.length <= 1) return prev;
                const next = prev.filter((s) => s !== id);
                savePreferences(config, next);
                return next;
            });
        },
        [config],
    );

    return { visibleStats, toggleStat };
}
